import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { TooltipContent } from "@/components/ui/tooltip-content";
import { getProfileAvatar } from "@/lib/account/profile";
import { signOut } from "@/lib/auth/auth";
import { useUser } from "@/lib/auth/session";
import { SITE_LOGO } from "@/lib/constants";
import { cn } from "@/lib/utils";
import {
	CircleUserRoundIcon,
	LogOut,
	type LucideIcon,
	SettingsIcon,
	ShieldQuestionMarkIcon,
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

interface menuItem {
	id: string;
	label: string;
	href?: string;
	icon?: LucideIcon;
	onClick?: () => void;
	group?: string;
	kbd?: string;
}

const menuOptions: menuItem[] = [
	{
		id: "profile",
		label: "Profile",
		href: "/profile",
		icon: CircleUserRoundIcon,
		group: "My Account",
		kbd: "⌘P",
	},
	{
		id: "settings",
		label: "Settings",
		href: "/account/settings",
		icon: SettingsIcon,
		group: "My Account",
		kbd: "⌘S",
	},
	{
		id: "help",
		label: "Help",
		href: "/help",
		icon: ShieldQuestionMarkIcon,
		group: "My Account",
		kbd: "⌘H",
	},
	{
		id: "logout",
		label: "Log Out",
		onClick: () => {
			signOut();
		},
		icon: LogOut,
		kbd: "⇧⌘Q",
	},
];

type ProfileMenuProps = {
	className?: string;
};

export function ProfileMenu({ className }: ProfileMenuProps) {
	const { user, isUserLoading, error } = useUser();
	const router = useRouter();

	const addedGroups = new Set<string>();
	const [avatarUrl, setAvatarUrl] = useState<string>(SITE_LOGO);
	const [isAvatarLoading, setIsAvatarLoading] = useState(false);
	const [avatarError, setAvatarError] = useState<Error | null>(null);

	useEffect(() => {
		// If user is still loading, do nothing
		if (isUserLoading) return;

		// If there's an error loading the user, log it
		if (error) {
			console.error("Error loading user:", error);
			return;
		}

		// If no user is found, we'll show the sign-up button
		if (!user) return;

		// Fetch avatar when we have a valid user
		let isMounted = true;
		const fetchAvatar = async () => {
			try {
				setIsAvatarLoading(true);
				const { avatarUrl, error } = await getProfileAvatar(user.id);

				if (!isMounted) return;

				if (error) {
					throw new Error(error.message || "Failed to load avatar");
				}

				setAvatarUrl(avatarUrl);
			} catch (err) {
				if (isMounted) {
					const error =
						err instanceof Error ? err : new Error("Failed to load avatar");
					console.error("Error loading avatar:", error);
					setAvatarError(error);
				}
			} finally {
				if (isMounted) {
					setIsAvatarLoading(false);
				}
			}
		};

		fetchAvatar();

		return () => {
			isMounted = false;
		};
	}, [user, isUserLoading, error]);

	if (isUserLoading) {
		return <Spinner className="size-3 self-center" />;
	}
	if (error) {
		console.error(error);
	}

	if (!user)
		return (
			<Link
				href="/auth/sign-up"
				className={cn(
					"flex rounded-2xl px-3 py-1.5 text-sm text-nowrap font-extrabold text-primary bg-primary/20",
					"shadow-xs transition-colors border border-primary-34 hover:bg-primary hover:text-background cursor-pointer",
				)}
			>
				Join
			</Link>
		);

	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				asChild
				className={cn(
					"flex items-center justify-center cursor-pointer",
					className,
				)}
			>
				<Avatar className="size-8">
					<TooltipContent content="Open profile menu">
						<AvatarImage
							src={avatarUrl}
							alt={"User avatar"}
							className={cn(
								"rounded-full transition-all border-2 border-primary/20",
								"hover:border-primary hover:scale-102",
							)}
						/>
					</TooltipContent>
					<AvatarFallback>ZM</AvatarFallback>
				</Avatar>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="w-48 mr-2" align="start">
				{menuOptions.map((item) => (
					<div key={item.id}>
						{item.group &&
							!addedGroups.has(item.group) &&
							addedGroups.add(item.group) && (
								<DropdownMenuLabel key={item.group}>
									{item.group}
								</DropdownMenuLabel>
							)}

						{!item.group && <DropdownMenuSeparator key={item.label} />}

						<DropdownMenuItem
							onClick={() => {
								item.onClick?.();
								router.refresh();
							}}
						>
							<Link
								href={item.href || "#"}
								className="w-full flex items-center justify-between gap-2 cursor-pointer"
							>
								{item.icon && (
									<item.icon className="size-4 fill-white/30 group-hover:stroke-[3px]" />
								)}
								{item.label}
								{item.kbd && (
									<DropdownMenuShortcut>{item.kbd}</DropdownMenuShortcut>
								)}
							</Link>
						</DropdownMenuItem>
					</div>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
